import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/LanguageContext";
import Header from "@/components/Header";
import { ExternalLink, ChevronDown, ChevronUp } from "lucide-react";

const publicationsByYear = {
  2026: [
    { authors: "Schwarze, S. A., Lindenberger, U., Bunge, S. A., & Fandakova, Y.", title: "Modular functional brain network organization contributes to training-related changes in task switching in children", journal: "bioRxiv", link: "https://www.biorxiv.org/content/10.64898/2026.02.26.708262v1" }
  ],
  2025: [
    { authors: "Fandakova, Y., Akin, G.", title: "Memory contributions to the development of cognitive flexibility in childhood", journal: "European Psychologist", link: "https://econtent.hogrefe.com/doi/full/10.1027/1016-9040/a000569" },
    { authors: "Fandakova, Y., Raz, N., Lindenberger, U., Khatib, D., Rajan, U., & Stanley, J. A.", title: "Developmental Changes in Brain Cellular Membrane and Energy Metabolism", journal: "Journal of Neuroscience, 45(30)", link: "https://www.jneurosci.org/content/jneuro/45/30/e2222242025.full.pdf" },
    { authors: "Schwarze, S. A., Bonati, S., Cichy, R. M., Lindenberger, U., Bunge, S. A., & Fandakova, Y.", title: "Task-Switch Related Reductions in Neural Distinctiveness in Children and Adults", journal: "Journal of Neuroscience, 45(26)", link: "https://www.jneurosci.org/content/45/26/e2358232025" },
    { authors: "Schwarze, S. A., Laube, C., Khosravani, N., Lindenberger, U., Bunge, S. A., & Fandakova, Y.", title: "Intensive task-switching training and single-task training differentially affect behavioral and neural manifestations", journal: "Cerebral Cortex, 35(5)", link: "https://doi.org/10.1093/cercor/bhaf103" },
    { authors: "Horn, S., Fandakova, Y., & Shing, Y. L.", title: "The interplay of memory and decision making – developmental and aging processes", journal: "European Psychologist" },
    { authors: "Fandakova, Y., & Lindenberger, U.", title: "Normal aging of frontal lobes and executive functions", journal: "Encyclopedia of the Human Brain (2nd ed.)" }
  ],
  2024: [
    { authors: "Fandakova, Y., Wenger, E.", title: "Skill learning in the developing brain: Interactions of control and representation systems", journal: "Psychology of Learning and Motivation, 81, 1-40", link: "https://www.sciencedirect.com/science/article/abs/pii/S0079742124000379" },
    { authors: "Fandakova, Y., Dennis, N.A.", title: "Introduction to the special issue: the neuroscience of false memory", journal: "Memory, 32(10), 1267-1270", link: "https://www.tandfonline.com/doi/full/10.1080/09658211.2024.2418768" },
    { authors: "Schwarze, S.A., Fandakova, Y., Lindenberger, U.", title: "Cognitive flexibility across the lifespan: developmental differences in the neural basis", journal: "Current Opinion in Behavioral Sciences, 58", link: "https://www.sciencedirect.com/science/article/pii/S2352154624000469" },
    { authors: "Stanciu, O., Jones, A., Metzner, N., Fandakova, Y., Ruggeri, A.", title: "The differential impact of active learning on children's memory", journal: "Developmental Psychology, 60(5), 904–915", link: "https://psycnet.apa.org/record/2024-58386-001" }
  ],
  2023: [
    { authors: "Schwarze, S. A., Laube, C., Khosravani, N., Lindenberger, U., Bunge, S. A., & Fandakova, Y.", title: "Does prefrontal connectivity during task switching help or hinder children's performance?", journal: "Developmental Cognitive Neuroscience, 60, 101217", link: "https://www.sciencedirect.com/science/article/pii/S1878929323000221" }
  ],
  2022: [
    { authors: "Broeker, L., et al.", title: "Individual differences fill the uncharted intersections between cognitive structure, flexibility, and plasticity", journal: "Psychological Review, 129(6), 1486–1494", link: "https://psycnet.apa.org/record/2022-77901-001" },
    { authors: "Buhl M, Akin G, et al.", title: "Expert validation of prediction models for a clinical decision-support system in audiology", journal: "Frontiers in Neurology, 13, 960012", link: "https://www.frontiersin.org/journals/neurology/articles/10.3389/fneur.2022.960012/full" }
  ],
  2021: [
    { authors: "Fandakova, Y., Johnson, E., & Ghetti, S.", title: "Distinct neural mechanisms underlie subjective and objective recollection", journal: "eLife, 10, e62520", link: "https://elifesciences.org/articles/62520" },
    { authors: "Gruber, M. & Fandakova, Y.", title: "Curiosity in development – what can we learn from the brain?", journal: "Current Opinion in Behavioral Sciences, 39, 178-184", link: "https://www.sciencedirect.com/science/article/pii/S2352154621000796" },
    { authors: "Fandakova, Y. & Gruber, M.", title: "States of curiosity and interest enhance memory differently in adolescents and in children", journal: "Developmental Science, 24(1), e13005", link: "https://onlinelibrary.wiley.com/doi/full/10.1111/desc.13005" }
  ],
  2020: [
    { authors: "Ghetti, S. & Fandakova, Y.", title: "Neural development of memory and metamemory", journal: "Annual Review of Developmental Psychology, 2, 365-388", link: "https://www.annualreviews.org/content/journals/10.1146/annurev-devpsych-060320-085634" },
    { authors: "Fandakova, Y., & Hartley, C. A.", title: "Mechanisms of learning and plasticity in childhood and adolescence", journal: "Developmental Cognitive Neuroscience, 42, 100764", link: "https://www.sciencedirect.com/science/article/pii/S1878929320300128" },
    { authors: "Fandakova, Y., Werkle-Bergner, M. & Sander, M. C.", title: "(Only) time can tell: Age differences in false memory are magnified at longer delays", journal: "Psychology and Aging, 35(4), 473-483", link: "https://psycnet.apa.org/buy/2020-23564-001" }
  ]
};

const years = Object.keys(publicationsByYear).sort((a, b) => b - a);

export default function PublicationsPage() {
  const { t, language } = useLanguage();
  const [expandedYears, setExpandedYears] = useState([2026, 2025, 2024]);

  const toggleYear = (year) => {
    setExpandedYears(prev => 
      prev.includes(year) 
        ? prev.filter(y => y !== year)
        : [...prev, year]
    );
  };

  return (
    <div className="min-h-screen bg-sand-50">
      <Header />

      <main className="px-6 md:px-12 py-12 max-w-4xl mx-auto">
        <h1 className="font-outfit text-3xl sm:text-4xl font-medium text-forest-900 mb-3">
          {t("publications.title")}
        </h1>
        <p className="text-lg text-stone-600 mb-6">
          {t("publications.subtitle")}
        </p>

        {/* Publications by Year */}
        <h2 className="font-outfit text-xl font-medium text-forest-800 mb-4">
          {t("publications.journalArticles")}
        </h2>

        <div className="space-y-3">
          {years.map((year) => (
            <Card key={year} className="bg-white rounded-xl border-stone-200 overflow-hidden">
              <button
                onClick={() => toggleYear(parseInt(year))}
                className="w-full px-5 py-4 flex items-center justify-between hover:bg-stone-50 transition-colors"
              >
                <span className="font-outfit font-medium text-forest-900">{year}</span>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-stone-500">{publicationsByYear[year].length} {language === "de" ? "Artikel" : "articles"}</span>
                  {expandedYears.includes(parseInt(year)) ? (
                    <ChevronUp className="h-4 w-4 text-stone-400" />
                  ) : (
                    <ChevronDown className="h-4 w-4 text-stone-400" />
                  )}
                </div>
              </button>
              
              {expandedYears.includes(parseInt(year)) && (
                <div className="px-5 pb-4 space-y-4 border-t border-stone-100">
                  {publicationsByYear[year].map((pub, index) => (
                    <div key={`${year}-${pub.title?.slice(0,20) || index}`} className="pt-4">
                      <p className="text-sm text-stone-500 mb-1">{pub.authors}</p>
                      <p className="text-stone-900 font-medium mb-1">
                        {pub.link ? (
                          <a href={pub.link} target="_blank" rel="noopener noreferrer" className="hover:text-forest-700 inline-flex items-center gap-1">
                            {pub.title}
                            <ExternalLink className="h-3 w-3" />
                          </a>
                        ) : pub.title}
                      </p>
                      <p className="text-sm text-stone-500 italic">{pub.journal}</p>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          ))}
        </div>
      </main>

      {/* Footer */}
      <footer className="py-6 px-6 md:px-12 border-t border-stone-200 mt-12">
        <div className="max-w-7xl mx-auto flex items-center justify-center">
          <p className="text-stone-500 text-xs">© 2026 Universität Trier. Learning Brain Lab</p>
        </div>
      </footer>
    </div>
  );
}
